// FT-108 (сводка v2): состав суммы брони для шапки формы редактирования.
// Заменяет прозу «Клиент: …, суток аренды: 3, цена аренды за сутки: 4000,
// всего за аренду: 12000. Итого: 12000 + 10000 = 22000» строкой слагаемых.
// Вынесено модулем по прецеденту editPanels.js/addonsMath.js: чистая функция
// тестируется без mount 13k-строчного компонента.
//
// Клиент, тариф, даты и скидка из сводки убраны намеренно — их показывают
// заголовки панелей ниже (ft108ClientValue/ft108DatesValue/ft108TariffValue),
// и повторять их в шапке значит держать два источника одной правды.

// Витринное округление в одной точке: grand считается сложением, а 0.1 + 0.2
// в двоичной арифметике даёт 0.30000000000000004 — на 20px-акценте итога это
// видно. Шаблон форматированием не занимается (ревью 2026-08-11).
export function money(value) {
  return Math.round(toMoney(value) * 100) / 100;
}

// Деньги приходят то числом (с сервера), то строкой ('5' из v-text-field).
// Для витрины пустая строка/null — ноль (в отличие от сравнения и payload,
// см. normalizeMoney в addonsMath.js).
export function toMoney(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return 0;
}

// Единица аренды определяет, что стоит в «3 × 4000»: сутки, часы, недели или
// месяцы. Флаги брони взаимоисключающие, порядок проверки — как в шаблоне.
const RENT_UNITS = [
  { unit: "hourly", count: "hourly_hours" },
  { unit: "weekly", count: "weekly_weeks" },
  { unit: "monthly", count: "monthly_months" },
];

export function rentUnit(booking) {
  const source = booking || {};
  const found = RENT_UNITS.find((item) => source[item.unit]);
  return found ? found.unit : "daily";
}

function rentCountField(unit) {
  const found = RENT_UNITS.find((item) => item.unit === unit);
  return found ? found.count : "days";
}

// Слагаемые сверх аренды. Порядок — от самых частых к редким; нулевые не
// показываются (строка шапки должна оставаться одной строкой).
const COMPONENTS = [
  { key: "add_hours", field: "hours_cost" },
  { key: "insurance", field: "insurance" },
  { key: "add_drivers", field: "add_drivers_cost" },
  { key: "equipment", field: "equipment" },
  { key: "addons", field: "addons_cost" },
  { key: "delivery", field: "delivery" },
  { key: "checkout", field: "delivery_end" },
  { key: "clean", field: "clean_payment" },
  { key: "damages", field: "damage" },
  { key: "fines", field: "fine" },
  { key: "other", field: "other" },
  // Поля приёмки и пробега: редактируются в этой же форме и входят в платёжный
  // итог (paymentStatus.js), но в первой версии списка их не было — шапка
  // молча прятала начисления и расходилась с total (ревью 2026-08-11).
  { key: "hours_cost_end", field: "hours_cost_end" },
  { key: "other_end", field: "other_end" },
  { key: "mileage_cost", field: "mileage_cost" },
  { key: "gas", field: "gas" },
];

// Равенство проверяется ровно в тех числах, которые печатаются, и допускается
// ТОЛЬКО погрешность двоичного представления: 3 × 33.33 даёт 99.99000000000001
// против сохранённых 99.99 — это одно и то же число, строгое === прятало бы
// верный расчёт. Копеечный допуск (0.01) для этого не годится: в ручном режиме
// цену и стоимость аренды печатает оператор с клавиатуры, и пара 3 × 4000.003
// при rental_cost 12000 разошлась бы на 0.009 — меньше копейки, но на экране
// это ложное равенство напечатанных чисел.
function isExactProduct(count, price, total) {
  if (!(count > 0) || !(price > 0)) return false;
  const product = count * price;
  return Math.abs(product - total) < 1e-9 * Math.max(1, Math.abs(total));
}

// labels — { rent, add_hours, … }: переводы приходят параметром, модуль не
// знает про i18n. deposit — из currentDeposit() компонента (у часовой/недельной
// /месячной аренды залог лежит в своём поле).
//
// Итог намеренно НЕ пересчитывается из слагаемых: booking.total — то, что
// сохранится и уйдёт в оплату, а в ручном/замороженном режиме он расходится с
// суммой полей (оператор вписал итог руками). Витрина показывает оба факта, а
// не подменяет один другим.
export function buildBookingSummary(booking, options) {
  const source = booking || {};
  const { labels = {}, deposit = 0 } = options || {};
  const unit = rentUnit(source);
  const count = toMoney(source[rentCountField(unit)]);
  // Цена округляется ДО проверки равенства: печатаются те же числа, что
  // перемножаются, иначе в строке равенства всплывал 3333.3333333333335
  const price = money(source.selected_price);

  const rent = money(source.rental_cost);
  const parts = [
    {
      key: "rent",
      label: labels.rent,
      value: rent,
      // Количество единиц и цена за единицу отдаются ВСЕГДА, отдельными
      // фактами: при неоднородной цене равенство ниже ложно, а прежняя проза
      // печатала эти два числа всегда, и в свёрнутой форме взять их больше
      // негде — заголовки панелей их не показывают (ревью 2026-08-11).
      count,
      price,
      // «3 × 4000 = 12000» — равенство, а не подпись, поэтому показывается
      // ТОЛЬКО когда оно верно. Цена аренды не всегда однородна: сезонные
      // сегменты и продления по другой цене (FT-084, плавающая база) держат
      // свои цены, и произведение суток на цену дня расходится с rental_cost.
      // Прежняя проза перечисляла те же числа подписями и ничего не
      // утверждала — формат равенства утверждает, поэтому проверяем.
      // Разложение по сегментам показывает раскрывашка «Сезоны» под шапкой.
      math: isExactProduct(count, price, rent) ? `${count} × ${price}` : "",
      unit,
    },
  ];

  COMPONENTS.forEach((component) => {
    const value = toMoney(source[component.field]);
    if (value !== 0) {
      parts.push({ key: component.key, label: labels[component.key], value: money(value), math: "" });
    }
  });

  const total = money(source.total);
  const depositValue = money(deposit);
  // Залог — обычное слагаемое, а не сноска у итога: это сумма, которую оператор
  // называет клиенту вслух, и в 12px-подписи она была самым мелким текстом шапки.
  if (depositValue !== 0) {
    parts.push({ key: "deposit", label: labels.deposit, value: depositValue, math: "" });
  }

  // Сумма слагаемых обычно и есть итог — тогда пояснять нечего. Расходится она
  // в ручном режиме (оператор вписал итог напрямую) и при скидке; только в этом
  // случае показываем, сколько составляет аренда без залога, иначе шапка молча
  // предъявляла бы числа, которые не складываются.
  const partsSum = parts.reduce((acc, part) => acc + part.value, 0);
  const grand = money(total + depositValue);

  return {
    parts,
    total,
    deposit: depositValue,
    grand,
    partsExplainGrand: Math.abs(partsSum - grand) < 1e-9 * Math.max(1, Math.abs(grand)),
  };
}
